import { Button, Drawer, Input, Select, Table, Tag, Tooltip } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import SectionWrapper from "../../components/SectionWrapper";
import ShowTotal from "../../components/ShowTotal";
import { AppStateContext } from "../../context/AppContext";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/langHelper";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";

const formatCurrency = (value, currency = "INR") => {
  const amount = Number(value || 0);
  return `${currency === "INR" ? "Rs." : currency} ${amount.toLocaleString("en-IN")}`;
};

const getUserLabel = (user) => user?.name || user?.user_name || user?.email || "-";

function CartManagement() {
  const heading = "Cart Management";
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const requestRef = useRef(request);

  const [searchText, setSearchText] = useState("");
  const [abandonedFilter, setAbandonedFilter] = useState("");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchData = useCallback(
    (pageConfig = { current: 1, pageSize: 10 }) => {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pageConfig?.current || 1),
        pageSize: String(pageConfig?.pageSize || 10),
      });

      if (debouncedSearchText) params.set("search", debouncedSearchText);
      if (abandonedFilter) params.set("abandoned", abandonedFilter);

      requestRef.current({
        url: `${apiPath.cart}?${params.toString()}`,
        method: "GET",
        onSuccess: ({ data }) => {
          setLoading(false);
          setList(
            (data?.docs || []).map((item) => ({
              ...item,
              key: item._id,
            })),
          );
          setPagination((prev) => ({
            ...prev,
            current: pageConfig?.current || 1,
            pageSize: pageConfig?.pageSize || 10,
            total: data?.totalDocs || 0,
          }));
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error?.response?.data?.message || error?.message || "Failed to load carts", Severty.ERROR);
        },
      });
    },
    [abandonedFilter, debouncedSearchText],
  );

  useEffect(() => {
    setPageHeading(heading);
  }, [setPageHeading]);

  useEffect(() => {
    fetchData({ current: 1, pageSize: pagination.pageSize });
  }, [abandonedFilter, debouncedSearchText, fetchData, pagination.pageSize]);

  const columns = [
    {
      title: lang("User"),
      dataIndex: "userId",
      key: "userId",
      render: (_, { userId }) => (
        <span className="cap" title={userId?.email || ""}>
          {getUserLabel(userId)}
        </span>
      ),
    },
    {
      title: lang("Total Products"),
      dataIndex: "totalItems",
      key: "totalItems",
      render: (_, { totalItems }) => <span>{Number(totalItems || 0)}</span>,
    },
    {
      title: lang("Quantity"),
      dataIndex: "totalQuantity",
      key: "totalQuantity",
      render: (_, { totalQuantity }) => <span>{Number(totalQuantity || 0)}</span>,
    },
    {
      title: lang("Cart Value"),
      dataIndex: "finalAmount",
      key: "finalAmount",
      render: (_, record) => <span>{formatCurrency(record?.finalAmount, record?.currency)}</span>,
    },
    {
      title: lang("Status"),
      dataIndex: "isAbandoned",
      key: "isAbandoned",
      render: (_, { isAbandoned }) => <Tag color={isAbandoned ? "red" : "green"}>{isAbandoned ? "Abandoned" : "Active"}</Tag>,
    },
    {
      title: lang("Updated At"),
      dataIndex: "updated_at",
      key: "updated_at",
      render: (_, { updated_at }) => <span>{updated_at ? new Date(updated_at).toLocaleString() : "-"}</span>,
    },
    {
      title: lang("Action"),
      key: "action",
      render: (_, record) => (
        <Tooltip title={lang("View")} color={"purple"}>
          <Button className="primary_btn btnStyle" onClick={() => setSelected(record)}>
            {lang("View")}
          </Button>
        </Tooltip>
      ),
    },
  ];

  const itemColumns = [
    {
      title: lang("Product"),
      dataIndex: "name",
      key: "name",
      render: (_, record) => <span className="cap">{record?.name || record?.productId?.title || "-"}</span>,
    },
    {
      title: lang("SKU"),
      dataIndex: "sku",
      key: "sku",
      render: (_, { sku }) => <span>{sku || "-"}</span>,
    },
    {
      title: lang("Qty"),
      dataIndex: "quantity",
      key: "quantity",
      render: (_, { quantity }) => <span>{Number(quantity || 0)}</span>,
    },
    {
      title: lang("Price"),
      dataIndex: "price",
      key: "price",
      render: (_, record) => <span>{formatCurrency(record?.price, selected?.currency)}</span>,
    },
    {
      title: lang("Subtotal"),
      dataIndex: "subtotal",
      key: "subtotal",
      render: (_, record) => <span>{formatCurrency(record?.subtotal, selected?.currency)}</span>,
    },
    {
      title: lang("Available"),
      dataIndex: "isAvailable",
      key: "isAvailable",
      render: (_, { isAvailable }) => <Tag color={isAvailable ? "green" : "red"}>{isAvailable ? "Yes" : "No"}</Tag>,
    },
  ];

  return (
    <>
      <SectionWrapper
        cardHeading={heading}
        extra={
          <div className="w-100 text-head_right_cont flex-wrap gap-2">
            <div className="pageHeadingSearch">
              <Input.Search className="searchInput" placeholder={lang("Search by product, slug or coupon")} onChange={(e) => setSearchText(e.target.value)} allowClear />
            </div>
            <Select
              style={{ minWidth: 180 }}
              placeholder="Cart status"
              value={abandonedFilter}
              onChange={setAbandonedFilter}
              options={[
                { value: "", label: "All Carts" },
                { value: "false", label: "Active Carts" },
                { value: "true", label: "Abandoned Carts" },
              ]}
            />
          </div>
        }
      >
        <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
        <div className="table-responsive customPagination checkBoxSrNo">
          <Table
            loading={loading}
            columns={columns}
            dataSource={list}
            pagination={{
              current: pagination.current,
              defaultPageSize: 10,
              responsive: true,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSize: pagination.pageSize,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            onChange={(nextPagination) => fetchData(nextPagination)}
            className="ant-border-space"
          />
        </div>
      </SectionWrapper>

      <Drawer title="Cart Details" width={760} open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected ? (
          <>
            <div className="mb-3">
              <p>
                <strong>User:</strong> {getUserLabel(selected?.userId)}
              </p>
              <p>
                <strong>Total cart value:</strong> {formatCurrency(selected?.finalAmount, selected?.currency)}
              </p>
              <p>
                <strong>Updated:</strong> {selected?.updated_at ? new Date(selected.updated_at).toLocaleString() : "-"}
              </p>
            </div>
            <Table columns={itemColumns} dataSource={(selected?.items || []).map((item, index) => ({ ...item, key: `${item?.productId?._id || item?.productId}-${index}` }))} pagination={false} />
          </>
        ) : null}
      </Drawer>
    </>
  );
}

export default CartManagement;
